import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Loader2Icon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { authApi } from '@/api/auth'
import { ApiError } from '@/api/errors'
import { homePathFor } from '@/auth/guards'
import { portalNextPath } from '@/auth/redirect'
import { useAuthStore } from '@/auth/store'
import { MagicLinkForm } from '@/components/auth/magic-link-form'
import { AuthLayout } from '@/components/auth-layout'
import { browserLocale, msg, setCurrentLocale } from '@/i18n'
import { portalLocale } from '@/i18n/portal-language'

export const Route = createFileRoute('/connexion/lien')({
  component: MagicLinkPage,
})

/**
 * Le jeton lu une seule fois par chargement : sous StrictMode, l'effet
 * s'exécute deux fois, et un lien est à usage unique — le consommer deux
 * fois se refuserait soi-même.
 */
let pendingConsumption: {
  token: string
  promise: ReturnType<typeof authApi.consumeMagicLink>
} | null = null

function consumeOnce(token: string) {
  if (pendingConsumption?.token !== token) {
    pendingConsumption = { token, promise: authApi.consumeMagicLink(token) }
  }
  return pendingConsumption.promise
}

/**
 * `/connexion/lien#{jeton}` (lot 30) : le jeton est dans le fragment, pour
 * ne jamais finir dans un journal de serveur. On le lit, on l'**efface de
 * l'adresse** avant toute autre chose — il ne reste ni dans l'historique ni
 * dans une capture —, puis on le consomme et l'on mène à `next_path`.
 */
function MagicLinkPage() {
  const navigate = useNavigate()
  const signIn = useAuthStore((state) => state.signIn)
  const tokenRef = useRef<string | null>(null)
  if (tokenRef.current === null) {
    tokenRef.current = window.location.hash.replace(/^#/, '')
    if (window.location.hash) {
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search,
      )
    }
  }
  const [email, setEmail] = useState('')
  const [failure, setFailure] = useState<'invalid' | 'other' | null>(
    tokenRef.current === '' ? 'invalid' : null,
  )

  useEffect(() => {
    const token = tokenRef.current
    if (!token) return
    let cancelled = false
    consumeOnce(token)
      .then(async (session) => {
        if (cancelled) return
        signIn(session)
        await navigate({
          to: portalNextPath(session.next_path) ?? homePathFor(session.user.kind),
          replace: true,
        })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        setFailure(error instanceof ApiError && error.status === 401 ? 'invalid' : 'other')
      })
    return () => {
      cancelled = true
    }
  }, [navigate, signIn])

  // Lot 34 : la personne n'est pas encore connue — la langue de son navigateur.
  setCurrentLocale(portalLocale(browserLocale()))
  const t = msg().auth.magic
  if (failure) {
    return (
      <AuthLayout title={t.invalidTitle} description={failure === 'invalid' ? t.invalid : t.failed}>
        <div data-testid="lien-refuse">
          <MagicLinkForm
            email={email}
            onEmailChange={setEmail}
            nextPath={undefined}
            submitLabel={t.requestNew}
            onUsePassword={() => {
              void navigate({ to: '/login', search: { redirect: undefined } })
            }}
          />
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout title={msg().auth.signInTitle}>
      <p
        className="flex items-center gap-2 text-sm text-muted-foreground"
        data-testid="lien-en-cours"
      >
        <Loader2Icon className="size-4 animate-spin" aria-hidden="true" />
        {t.inProgress}
      </p>
    </AuthLayout>
  )
}
